import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MLabel, SinceRows } from "@/components/le/MinutesUI";
import { daDate } from "@/lib/date";
import { FollowupDialog, ST_BADGE } from "@/components/le/ActionUI";
import { AttachDialog } from "@/components/le/OriginPicker";
import { ChevronDown, ChevronRight, Plus, Link2 } from "lucide-react";
import { DotsMenu } from "@/components/le/SidePanel";
import { MStatusBadge } from "@/components/le/MinutesUI";

export function OriginLine({ action, className = "" }) {
  const line = action.origin?.line;
  if (!line) return null;
  return <p className={`text-[13px] text-stone-600 ${className}`} data-testid={`fc-origin-${action.id}`}>{line}</p>;
}

function FollowupText({ f }) {
  const chips = [];
  if (f.status_to) chips.push(`Status: ${f.status_from || "—"} → ${f.status_to}`);
  if ("deadline_to" in f) chips.push(`Frist → ${daDate(f.deadline_to)}`);
  if ("responsible_to" in f) chips.push(`Ansvarlig → ${f.responsible_to || "—"}`);
  return (
    <>
      {f.comment ? <p className="text-sm text-stone-700 whitespace-pre-wrap">{f.comment}</p> : null}
      {chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1">{chips.map((c, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">{c}</span>)}</div>}
    </>
  );
}

export function FollowupCard({ action: a, users, meeting, onChanged, readonly = false, badge = null, hideLatest = false, noLink = false, canAttach = true, children }) {
  const [hist, setHist] = useState(false);
  const [fu, setFu] = useState(false);
  const [attach, setAttach] = useState(false);
  const latest = a.latest_followup;
  const prev = Math.max(0, (a.followup_count || (a.followups || []).length) - 1);
  const titleCls = "block text-[15px] font-medium text-[#1A1919] mt-1.5 whitespace-pre-wrap";
  return (
    <div className="group/fc bg-white rounded-2xl border border-[#EBE8E3] p-4" data-testid={`fc-${a.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs text-stone-500">
        <span className="font-mono text-stone-400">{a.action_no}</span>
        {a.overdue && a.status !== "Lukket" ? <MStatusBadge overdue deadline={a.deadline} testid={`fc-status-${a.id}`} /> : <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ST_BADGE[a.status] || ""}`} data-testid={`fc-status-${a.id}`}>{a.status}</span>}
        <span>Frist {a.deadline ? daDate(a.deadline) : "—"}</span>
        <span>·</span><span>{a.responsible_name || "—"}</span>
        {badge}
      </div>
      {noLink ? <p className={titleCls} data-testid={`fc-title-${a.id}`}>{a.description}</p>
        : <Link to={`/handling/${a.id}`} className={`${titleCls} hover:text-[#A83232]`} data-testid={`fc-title-${a.id}`}>{a.description}</Link>}
      <OriginLine action={a} className="mt-0.5" />
      {children}
      {!hideLatest && (latest ? (
        <div className="mt-3 rounded-xl px-3 py-2 border bg-[#FBFAF8] border-[#F0EDE8]" data-testid={`fc-latest-${a.id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#806B61]">Seneste opfølgning <span className="font-normal normal-case tracking-normal text-stone-500">· {daDate(latest.at)} · {latest.by_name || "—"}</span></p>
          <div className="mt-0.5"><FollowupText f={latest} /></div>
        </div>
      ) : <p className="mt-2 text-sm text-stone-500 italic" data-testid={`fc-latest-${a.id}`}>Ingen opfølgning endnu</p>)}
      <div className="flex items-center justify-between gap-2 mt-2.5 text-xs">
        {prev > 0 ? (
          <button onClick={() => setHist((h) => !h)} className="text-[#806B61] hover:text-[#A83232] flex items-center gap-1" data-testid={`fc-history-${a.id}`}>{hist ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {prev} tidligere {prev === 1 ? "opfølgning" : "opfølgninger"}</button>
        ) : <span />}
        <span className="hidden md:flex items-center gap-2 opacity-0 group-hover/fc:opacity-100 focus-within:opacity-100 transition-opacity">
          {!readonly && canAttach && (a.origin?.missing_point || a.origin?.missing_source) && <button onClick={() => setAttach(true)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#D6D3D1] px-3 py-1 text-stone-600 hover:border-stone-700 hover:text-stone-900 transition-colors" data-testid={`fc-attach-${a.id}`}><Link2 className="h-3.5 w-3.5" /> Tilknyt</button>}
          {!readonly && <button onClick={() => setFu(true)} className="inline-flex items-center gap-1 rounded-full border border-[#EBE8E3] px-3 py-1 text-[#1A1919] hover:border-stone-700 transition-colors" data-testid={`fc-add-${a.id}`}><Plus className="h-3.5 w-3.5" /> Opfølgning</button>}
        </span>
        {!readonly && <span className="md:hidden"><DotsMenu testid={`fc-menu-${a.id}`} items={[canAttach && (a.origin?.missing_point || a.origin?.missing_source) && ["Tilknyt", () => setAttach(true)], ["+ Opfølgning", () => setFu(true)]]} /></span>}
      </div>
      {hist && (
        <div className="mt-2 border-l-2 border-[#EBE8E3] pl-3 space-y-2" data-testid={`fc-history-list-${a.id}`}>
          {(a.followups || []).slice(0, -1).reverse().map((f) => (
            <div key={f.id}><p className="text-[11px] text-stone-400">{daDate(f.at)} · {f.by_name || "—"}{f.meeting_title ? ` · ${f.meeting_title}` : ""}</p><FollowupText f={f} /></div>
          ))}
        </div>
      )}
      {fu && <FollowupDialog open onOpenChange={setFu} users={users} action={a} meeting={meeting} onSaved={onChanged} />}
      {attach && <AttachDialog action={a} onClose={() => setAttach(false)} onSaved={onChanged} />}
    </div>
  );
}

export function FollowupCardCompact({ action: a, noLink = false }) {
  const cls = "block rounded-xl border border-[#EBE8E3] px-3 py-2.5 transition-colors";
  const inner = (
    <>
      <div className="flex items-center gap-2 flex-wrap text-xs text-stone-500">
        <span className="font-mono text-stone-400">{a.action_no}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ST_BADGE[a.status] || ""}`}>{a.status}</span>
        <span className={a.overdue ? "text-[#A83232] font-medium" : ""}>Frist {a.deadline ? daDate(a.deadline) : "—"}</span><span>·</span><span>{a.responsible_name || "—"}</span>
      </div>
      <p className="text-sm font-medium text-[#1A1919] mt-1">{a.description}</p>
      <OriginLine action={a} className="mt-0.5" />
      <p className="text-xs mt-1 line-clamp-2">{a.latest_followup ? <><span className="text-stone-500">Seneste opfølgning {daDate(a.latest_followup.at)}: </span><span className="text-stone-700">{a.latest_followup.comment || (a.latest_followup.status_to ? `Status → ${a.latest_followup.status_to}` : "—")}</span></> : <span className="text-stone-500 italic">Ingen opfølgning endnu</span>}</p>
    </>
  );
  if (noLink) return <div data-testid={`fc-compact-${a.id}`} className={cls}>{inner}</div>;
  return <Link to={`/handling/${a.id}`} data-testid={`fc-compact-${a.id}`} className={`${cls} hover:border-stone-300`}>{inner}</Link>;
}

export const EVENT_LABEL = { created: "Ny handling", followup: "Opfølgning", status: "Statusændring", closed: "Lukket" };
const EVENT_CLS = { created: "bg-[#E7EADF] text-[#5f6b4a]", followup: "bg-[#F4EFEC] text-[#806B61]", status: "bg-[#EFE7DC] text-[#8A6D3B]", closed: "bg-[#E7EADF] text-[#5f6b4a]" };

export function SinceEvents({ a }) {
  return (
    <div className="mt-2.5 border-l-2 border-[#E3D9C9] pl-3 space-y-1.5" data-testid={`le-since-events-${a.id}`}>
      {(a.since_events || []).map((e, i) => (
        <div key={i} className="text-xs">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold mr-1.5 ${EVENT_CLS[e.kind]}`}>{EVENT_LABEL[e.kind]}</span>
          <span className="text-stone-500">{daDate(e.at)} · {e.by_name || "—"}{e.kind === "created" && e.outside_le ? " · uden for LE" : ""}{e.in_meeting ? " · på mødet" : ""}</span>
          {e.kind === "status" && <span className="text-stone-600 ml-1.5">{e.status_from || "—"} → {e.status_to}</span>}
          {e.kind === "closed" && (e.comment ? <p className="text-stone-700 mt-0.5"><span className="text-[#5f6b4a] font-medium">Løsning: </span>{e.comment}</p> : <p className="text-stone-500 italic mt-0.5">Lukket uden løsningsbeskrivelse</p>)}
          {e.kind !== "closed" && e.comment && <p className="text-stone-700 mt-0.5 whitespace-pre-wrap">{e.comment}</p>}
        </div>
      ))}
    </div>
  );
}

export function SinceSection({ since, users, meeting, onChanged, readonly = false, noLink = false, testid = "le-since" }) {
  const nav = useNavigate();
  const uniq = (arr) => { const s = new Set(); return arr.filter((a) => !s.has(a.id) && s.add(a.id)); };
  const items = since?.items || [];
  const moved = uniq(items.filter((a) => a.status !== "Lukket")), closed = uniq(items.filter((a) => a.status === "Lukket"));
  const open = noLink ? undefined : (a) => nav(`/handling/${a.id}`);
  return (
    <>
      {moved.length === 0 && closed.length === 0 && <div className="bg-white rounded-2xl border border-[#EBE8E3] p-8 text-center text-stone-400">Intet er sket på handlingsplanen siden forrige møde.</div>}
      {moved.length > 0 && <div data-testid={`${testid}-list`}><MLabel className="mb-3">Sket siden sidst ({moved.length})</MLabel><SinceRows items={moved} onOpen={open} testid={`${testid}-rows`} /></div>}
      {closed.length > 0 && <div className={moved.length ? "mt-8" : ""} data-testid={`${testid}-closed`}><MLabel className="mb-3">Lukket ({closed.length})</MLabel><SinceRows items={closed} closed onOpen={open} testid={`${testid}-closed-rows`} /></div>}
    </>
  );
}
